import type { TrackEvent } from "@salestrak/analytics-taxonomy";

/**
 * The analytics session for code that cannot call `useAnalytics()`: a
 * module-level store such as the dialog emitter or the hotkey dispatcher, and
 * the auth provider, which sits above `AnalyticsProvider` in the tree.
 * `AnalyticsProvider` attaches its client here while it is mounted; until
 * then each call is a no-op, so a caller never has to check.
 */
type AnalyticsSession = {
  track: (event: TrackEvent) => void;
  flushAll: () => Promise<void>;
  reset: () => void;
};

let session: AnalyticsSession | null = null;

// A slow or dead analytics API must not hold up sign-out.
const SIGN_OUT_FLUSH_TIMEOUT_MS = 1_500;

export function attachAnalyticsSession(next: AnalyticsSession): () => void {
  session = next;
  return () => {
    if (session === next) session = null;
  };
}

/** Track from outside React. */
export function trackEvent(event: TrackEvent): void {
  session?.track(event);
}

/**
 * Send every queued event while the tokens are still valid. Bounded and
 * never-throwing, so sign-out always goes on.
 */
export async function flushAnalyticsBeforeSignOut(): Promise<void> {
  if (!session) return;
  await Promise.race([
    session.flushAll().catch(() => {}),
    new Promise<void>((resolve) => setTimeout(resolve, SIGN_OUT_FLUSH_TIMEOUT_MS)),
  ]);
}

/** Drop what is left, so none of it goes out under the next user. */
export function resetAnalyticsSession(): void {
  session?.reset();
}
